import { CAPTURE_TYPE } from '@nolens/ui';
import { useEffect, useState } from 'react';
import { ApiError, apiBlob } from './api';
import { config } from './config';
import { forgetImageAccess, imageAccessFor } from './imageAccess';

/**
 * Capturas privadas (o pedido leva a sessão): a imagem fica em memória e mostra-se por um
 * endereço blob:. Quem precisa dos bytes (desfocar) usa guideImageBlob: a CSP da app não
 * deixa fazer fetch de blob: (connect-src). Uma falha não fica em memória: o pedido seguinte
 * volta à API.
 */
const cache = new Map<string, Promise<{ blob: Blob; url: string }>>();

/** Pelo endereço assinado, se houver (null = usar a sessão). */
async function signedBlob(path: string, guideId: string, imageId: string): Promise<Blob | null> {
  const q = imageAccessFor(guideId, imageId);
  if (!q) return null;
  try {
    const res = await fetch(`${config.apiUrl}${path}?${q}`);
    if (res.ok) return await res.blob();
  } catch {
    // Sem rede: a rota com sessão dá o erro certo (e as novas tentativas).
  }
  forgetImageAccess(guideId, imageId);
  return null;
}

function load(guideId: string, imageId: string): Promise<{ blob: Blob; url: string }> {
  const key = `${guideId}/${imageId}`;
  let p = cache.get(key);
  if (!p) {
    const path = `/v1/guides/${guideId}/images/${imageId}`;
    p = signedBlob(path, guideId, imageId)
      .then((signed) => signed ?? apiBlob(path))
      .then((blob) => ({
        blob,
        url: URL.createObjectURL(blob),
      }));
    p.catch(() => cache.delete(key));
    cache.set(key, p);
  }
  return p;
}

/** Os bytes de uma captura (da memória, se já foi mostrada). */
export const guideImageBlob = (guideId: string, imageId: string): Promise<Blob> =>
  load(guideId, imageId).then((x) => x.blob);

/**
 * Imagem escolhida para substituir uma captura: até 2560 px de largura, no formato das
 * capturas. Devolve também o tamanho (o passo passa a ter a proporção da imagem nova).
 */
export async function prepareReplacement(
  file: Blob,
): Promise<{ blob: Blob; width: number; height: number }> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, 2560 / bmp.width);
  const width = Math.max(1, Math.round(bmp.width * scale));
  const height = Math.max(1, Math.round(bmp.height * scale));
  const cv = new OffscreenCanvas(width, height);
  cv.getContext('2d')!.drawImage(bmp, 0, 0, width, height);
  bmp.close();
  return { blob: await cv.convertToBlob({ type: CAPTURE_TYPE, quality: 0.85 }), width, height };
}

/** Esperas entre tentativas de uma captura que não veio (cerca de 1 minuto no total). */
export const RETRY_DELAYS = [1000, 2000, 4000, 8000, 15_000, 30_000];

/**
 * Falhas que podem passar sozinhas: sem rede, sessão a renovar (401), captura ainda a
 * caminho (404: a extensão grava o guia antes de enviar as capturas), servidor ocupado.
 */
export function retryable(e: unknown): boolean {
  if (!(e instanceof ApiError)) return true;
  const s = e.status;
  return s === 0 || s === 401 || s === 404 || s === 408 || s === 429 || s >= 500;
}

/**
 * Pede uma captura até chegar: se falhar, tenta outra vez com esperas cada vez maiores
 * (enquanto o erro puder passar e houver esperas) e, quando a rede volta, tenta logo.
 * Devolve a função que pára.
 */
export function watchGuideImage(
  guideId: string,
  imageId: string,
  onUrl: (url: string) => void,
  delays: readonly number[] = RETRY_DELAYS,
): () => void {
  let stopped = false;
  let inflight = false;
  let tries = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const attempt = () => {
    timer = undefined;
    inflight = true;
    load(guideId, imageId).then(
      (x) => {
        inflight = false;
        if (stopped) return;
        stop();
        onUrl(x.url);
      },
      (e) => {
        inflight = false;
        if (stopped || !retryable(e) || tries >= delays.length) return;
        timer = setTimeout(attempt, delays[tries++]);
      },
    );
  };
  const online = () => {
    if (stopped || inflight) return;
    clearTimeout(timer);
    tries = 0;
    attempt();
  };
  const stop = () => {
    stopped = true;
    clearTimeout(timer);
    window.removeEventListener('online', online);
  };
  window.addEventListener('online', online);
  attempt();
  return stop;
}

export function useGuideImage(guideId: string, imageId: string | undefined): string | null {
  const [state, setState] = useState<{ key: string; url: string } | null>(null);
  const key = imageId ? `${guideId}/${imageId}` : '';
  useEffect(() => {
    if (!imageId) return;
    return watchGuideImage(guideId, imageId, (url) =>
      setState({ key: `${guideId}/${imageId}`, url }),
    );
  }, [guideId, imageId]);
  return state && state.key === key ? state.url : null;
}
